import { Tooltip, makeStyles } from '@fluentui/react-components';

import { MigrationGroupRollup } from '../api/migrationGroups';
import { STATUS } from '../theme/tokens';

// A group holds N migrations in N states, so a single StatusBadge would be a
// lie — it would have to pick one. A stacked bar shows the whole distribution
// in the width of a table cell.
//
// The dimension shown is WAVE-BPRT STATUS, not `Migrations.Status`: that column
// is written "active" at creation and no writer ever updates it, so a status
// chart built on it would read 100% active forever (FD-269).

const SEGMENTS = [
  // Order is lifecycle order, left to right, so the bar reads as progress.
  { key: 'notActivated', label: 'Not activated', color: '#d4d4d8' }, // zinc-300
  // FD-466 — `STATUS.underway`, not the brand accent. A chart segment carries
  // INFORMATION, so it must clear WCAG 1.4.11's 3:1 on white (this does, at
  // 3.73:1), and "a wave is running" is a status rather than a brand moment.
  // Under the orange ramp the two were hard to tell apart, so this segment used
  // the accent's graphical rung; under AirBridge the status vocabulary is the
  // right home and the value comes from the token module either way.
  //
  // It carried a hardcoded hex through two accent retirements — the lower-case
  // sky-700 spelling that a case-sensitive grep missed in FD-388, then the
  // orange the rebrand gate caught here — which is the argument for the import.
  { key: 'active', label: 'Wave active', color: STATUS.underway },
  { key: 'revoked', label: 'Wave completed', color: '#16a34a' }, // green-600
  { key: 'expired', label: 'BPRT expired', color: '#dc2626' }, // red-600
] as const;

const useStyles = makeStyles({
  bar: {
    display: 'flex',
    width: '100%',
    minWidth: '120px',
    height: '8px',
    borderTopLeftRadius: '4px',
    borderTopRightRadius: '4px',
    borderBottomLeftRadius: '4px',
    borderBottomRightRadius: '4px',
    overflowX: 'hidden',
    backgroundColor: '#f4f4f5', // zinc-100 — the empty track
  },
  segment: {
    height: '100%',
  },
  empty: {
    fontSize: '12px',
    color: '#a1a1aa', // zinc-400
  },
});

export interface GroupStatusBarProps {
  rollup: MigrationGroupRollup;
}

export default function GroupStatusBar({ rollup }: GroupStatusBarProps) {
  const styles = useStyles();

  const total = rollup.migrationCount;
  if (total <= 0) {
    // An empty group is an ordinary state, not a problem. A zero-width bar
    // would read as a rendering fault.
    return <span className={styles.empty}>No migrations</span>;
  }

  // Known segments first, then anything the server counted that this component
  // has not heard of — the rollup counts unrecognised values rather than
  // dropping them, so rendering only the known keys would make the bar lie
  // about its own total.
  const known = new Set<string>(SEGMENTS.map((s) => s.key));
  const extras = Object.entries(rollup.byBprtStatus ?? {}).filter(
    ([key, count]) => !known.has(key) && count > 0,
  );

  const parts = [
    ...SEGMENTS.map((s) => ({
      key: s.key,
      label: s.label,
      color: s.color,
      count: rollup.byBprtStatus?.[s.key] ?? 0,
    })),
    ...extras.map(([key, count]) => ({
      key,
      label: key,
      color: '#a1a1aa', // zinc-400 — visible, unstyled, obviously "other"
      count,
    })),
  ].filter((p) => p.count > 0);

  const summary = parts.map((p) => `${p.count} ${p.label.toLowerCase()}`).join(', ');

  return (
    <Tooltip relationship="label" content={summary} positioning="above">
      <div className={styles.bar} role="img" aria-label={summary}>
        {parts.map((p) => (
          <div
            key={p.key}
            className={styles.segment}
            style={{ width: `${(p.count / total) * 100}%`, backgroundColor: p.color }}
          />
        ))}
      </div>
    </Tooltip>
  );
}
